// Casino Dashboard › Overview
//
// Ported from casino_dashboard/src/layouts/tables — the six period summary
// tables (Deposit, Games Play, Cash Claims, Gift Claims, Users, All Withdrawal)
// built from a single GET /dashboard/admindata payload.
import React from "react";
import { Box, Grid, Button } from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import DataTable from "../../components/DataTable";
import DashboardService from "../../Utility/dashboardService";
import {
  PERIODS,
  ProfitChip,
  StateLine,
  errText,
  getDifferent,
  getTotal,
  money,
} from "./shared";

const periodColumn = { header: "", accessor: "duration", width: "35%", align: "left" };

// Build rows for the five period buckets; `cells` maps a period key to values.
const buildRows = (cells) =>
  PERIODS.map(({ label, key }) => ({ duration: label, ...cells(key) }));

const numberCell = (v) => (v == null ? "0" : String(v));

export default function CasinoOverview() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [tick, setTick] = React.useState(0);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const result = await DashboardService.getData("/dashboard/admindata");
        if (active) setData(result?.data ?? null);
      } catch (e) {
        if (active) setError(errText(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [tick]);

  const tables = React.useMemo(() => {
    if (!data) return null;
    const skrill = data.skrill || {};
    const at = (group, key) => (group ? group[key] : null);

    // Deposit — Skrill receipts plus the SC packages bought in the same window.
    const deposit = {
      columns: [
        periodColumn,
        { header: "Skrill", accessor: "skril", align: "left" },
        { header: "Total", accessor: "total", align: "center" },
        { header: "Buy SC", accessor: "totalsc", align: "center" },
      ],
      rows: buildRows((key) => ({
        skril: money(at(skrill.Deposit, key)),
        total: money(getTotal(at(skrill.Deposit, key), "0.0")),
        totalsc: numberCell(at(skrill.buyCoins, key)),
      })),
    };

    // Games Play — today reads the dedicated today* fields, as in the original.
    const bets = {
      columns: [
        periodColumn,
        { header: "Spin Coins", accessor: "betamt", align: "center" },
        { header: "Win Coins", accessor: "winbetamt", align: "center" },
        {
          header: "Profit/Loss",
          accessor: "profit",
          align: "center",
          render: (v) => <ProfitChip value={v} />,
        },
      ],
      rows: buildRows((key) => {
        let wagered;
        let won;
        if (key === "today") {
          wagered = data.todayBetWidhrawal;
          won = data.todayBetDeposit;
        } else if (key === "totalSum") {
          wagered = data.totalBetWidhrawal;
          won = data.totalBetDeposit;
        } else {
          wagered = getTotal(at(data.bet, key), "0.0");
          won = at(data.winbet, key);
        }
        return {
          betamt: money(wagered),
          winbetamt: money(won),
          profit: getDifferent(wagered, won),
        };
      }),
    };

    const claimColumns = [
      periodColumn,
      { header: "Processed", accessor: "processed", align: "left" },
      { header: "Pending", accessor: "pending", align: "center" },
      { header: "Total", accessor: "total", align: "center" },
    ];

    // Cash Claims — cash-out requests paid out via Skrill.
    const cashClaims = {
      columns: claimColumns,
      rows: buildRows((key) => ({
        processed: money(at(skrill.CashWithdraw, key)),
        pending: money(at(skrill.CashPending, key)),
        total: money(at(skrill.TotalCashWithdraw, key)),
      })),
    };

    // Gift Claims — gift-card redemptions.
    const giftClaims = {
      columns: claimColumns,
      rows: buildRows((key) => ({
        processed: money(at(skrill.Withdraw, key)),
        pending: money(at(skrill.Pending, key)),
        total: money(getTotal(at(skrill.AllWithdraw, key), "0.0")),
      })),
    };

    // Users — KYC funnel against total registrations.
    const users = {
      columns: [
        periodColumn,
        { header: "KYC Passed", accessor: "kycpass", align: "left" },
        { header: "KYC Failed", accessor: "kycfail", align: "center" },
        { header: "Pending", accessor: "kycpending", align: "center" },
        { header: "Total", accessor: "total", align: "center" },
      ],
      rows: buildRows((key) =>
        key === "totalSum"
          ? {
              kycpass: numberCell(data.kycPassUsers),
              kycfail: numberCell(data.kycFailedUsers),
              kycpending: numberCell(data.kycPendingUsers),
              total: numberCell(data.registeredUsers),
            }
          : {
              kycpass: numberCell(at(data.kycPass, key)),
              kycfail: numberCell(at(data.kycFail, key)),
              kycpending: numberCell(at(data.kycPending, key)),
              total: numberCell(at(data.users, key)),
            }
      ),
    };

    // All Withdrawal — cash claims and gift claims combined. The original omits
    // the Total row here, so this one only covers the four dated buckets.
    const allWithdrawal = {
      columns: claimColumns,
      rows: PERIODS.filter((p) => p.key !== "totalSum").map(({ label, key }) => {
        const processed = getTotal(at(skrill.CashWithdraw, key), at(skrill.Withdraw, key));
        const pending = getTotal(at(skrill.CashPending, key), at(skrill.Pending, key));
        return {
          duration: label,
          processed: money(processed),
          pending: money(pending),
          total: money(getTotal(processed, pending)),
        };
      }),
    };

    return { deposit, bets, cashClaims, giftClaims, users, allWithdrawal };
  }, [data]);

  const panels = tables
    ? [
        ["Deposit", tables.deposit],
        ["Games Play", tables.bets],
        ["Cash Claims", tables.cashClaims],
        ["Gift Claims", tables.giftClaims],
        ["Users", tables.users],
        ["All Withdrawal", tables.allWithdrawal],
      ]
    : [];

  return (
    <Box>
      <PageHeader
        title="Casino Overview"
        crumb="Casino Dashboard / Overview"
        subtitle="Deposits, game play, claims and registrations by period"
        actions={
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<RefreshRoundedIcon />}
            onClick={() => setTick((t) => t + 1)}
            disabled={loading}
          >
            Refresh
          </Button>
        }
      />

      <StateLine loading={loading} error={error} empty={!loading && !error && !data} />

      {tables && (
        <Grid container spacing={1.75} alignItems="flex-start">
          {panels.map(([title, table]) => (
            <Grid item xs={12} lg={6} key={title}>
              <SectionCard title={title} sx={{ p: 2, height: "auto" }}>
                <DataTable columns={table.columns} rows={table.rows} />
              </SectionCard>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
